import React, { ReactNode, useState } from "react";
import { MdOutlineVisibility, MdOutlineVisibilityOff } from "react-icons/md";
import classNames from "classnames";
import { FormikErrors, FormikTouched, getIn } from "formik";
import { twMerge } from "tailwind-merge";

interface TextFieldProps<T> {
  id: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<unknown>) => void;
  onBlur?: (e: React.ChangeEvent<unknown>) => void;
  touched?: FormikTouched<T>;
  errors?: FormikErrors<T>;
  hideable?: boolean;
  placeholder?: string;
  disabled?: boolean;
  label?: string; // Add label prop
  className?: string; // Add className prop for custom styling
}

const TextField: React.FC<TextFieldProps<unknown>> = ({
  id,
  value,
  onChange,
  onBlur,
  touched = {},
  errors = {},
  hideable = false,
  disabled = false,
  placeholder,
  label,
  className,
}) => {
  // hooks
  const [showHiddenText, setHiddenText] = useState<boolean>(!hideable);

  // classes
  const containerClasses = twMerge(
    classNames("w-full text-cDarkBlue relative flex flex-col rounded", className, {
      "opacity-50 cursor-not-allowed": disabled,
    })
  );

  const inputClasses = classNames(
    "w-full min-h-12 border border-gray-300 rounded-lg shadow-sm p-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all",
    {
       "border-red-500 focus:ring-red-500": getIn(errors, id) && getIn(touched, id),
    }
  );
  
  const errorMessageClasses = classNames("text-sm text-red-500 mt-1");

  return (
    <div className={containerClasses}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          id={id}
          type={showHiddenText || !hideable ? "text" : "password"}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          className={inputClasses}
          disabled={disabled}
        />

        {hideable && (
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
            onClick={() => setHiddenText(!showHiddenText)}
          >
            {showHiddenText ? (
              <MdOutlineVisibilityOff className="text-xl" />
            ) : (
              <MdOutlineVisibility className="text-xl" />
            )}
          </button>
        )}
      </div>

      {getIn(errors, id) && getIn(touched, id) && (
        <div className={errorMessageClasses}>
          {getIn(errors, id) as ReactNode}
        </div>
      )}
    </div>
  );
};

export default TextField;
